
import cornerstone from 'cornerstone-core';

window.__mprSliceCache = window.__mprSliceCache || {};

export function isCached(uid) {
  return !!(
    window.__mprSliceCache[uid + '_cor'] &&
    window.__mprSliceCache[uid + '_sag'] &&
    window.__mprSliceCache[uid + '_meta']
  );
}

export async function buildMPR(imageIds, uid) {
  if (isCached(uid)) return true;

  const images = [];
  for (const id of imageIds) {
    try { images.push({ img: await cornerstone.loadAndCacheImage(id), id }); }
    catch(e) {}
  }
  if (!images.length) return false;

  // Sort by Z ascending
  images.sort((a, b) => {
    const za = (((cornerstone.metaData.get('imagePlaneModule', a.id)||{}).imagePositionPatient)||[0,0,0])[2];
    const zb = (((cornerstone.metaData.get('imagePlaneModule', b.id)||{}).imagePositionPatient)||[0,0,0])[2];
    return za - zb;
  });

  const first = images[0];
  const rows = first.img.rows, columns = first.img.columns, depth = images.length;
  const meta0 = cornerstone.metaData.get('imagePlaneModule', first.id) || {};
  const rowSp = meta0.rowPixelSpacing || 1;
  const colSp = meta0.columnPixelSpacing || 1;

  let sliceSp = rowSp;
  if (images.length > 1) {
    const p0 = (((cornerstone.metaData.get('imagePlaneModule', images[0].id)||{}).imagePositionPatient)||[0,0,0]);
    const p1 = (((cornerstone.metaData.get('imagePlaneModule', images[1].id)||{}).imagePositionPatient)||[0,0,0]);
    const dz = Math.abs(p1[2] - p0[2]);
    if (dz > 0) sliceSp = dz;
  }

  let ww = first.img.windowWidth, wc = first.img.windowCenter;
  if (Array.isArray(ww)) ww = ww[0];
  if (Array.isArray(wc)) wc = wc[0];
  ww = ww || 400; wc = wc || 40;
  const dicomWW = ww, dicomWC = wc;
  const slope = first.img.slope || 1;
  const intercept = first.img.intercept || 0;

  const pxArrays = images.map(({ img }) => {
    const raw = typeof img.getPixelData === 'function' ? img.getPixelData() : img.pixelData;
    const out = new Int16Array(rows * columns);
    if (raw) for (let i = 0; i < out.length; i++) out[i] = raw[i];
    return out;
  });

  // COR: for each Y → image is (columns wide) x (depth tall)
  // scaledH corrects depth pixels to physical mm
  const corH = Math.max(1, Math.round(depth * sliceSp / colSp));

  // SAG: for each X → image is (rows wide) x (depth tall)
  const sagH = Math.max(1, Math.round(depth * sliceSp / rowSp));

  const workerCode = `
self.onmessage = function(e) {
  const { planes, rows, columns, depth, slope, intercept, corH, sagH } = e.data;

  // Store raw HU as Int16 — NO WL baking.
  // Cornerstone will apply window/level directly, same as axial.

  // CORONAL: for each Y row → image is columns wide x depth tall (Z flipped)
  const corBufs = [];
  for (let y = 0; y < rows; y++) {
    const buf = new Int16Array(columns * depth);
    for (let z = 0; z < depth; z++) {
      const plane = planes[z];
      const row = (depth - 1 - z); // flip: superior at top
      for (let x = 0; x < columns; x++) {
        const hu = plane[y * columns + x] * slope + intercept;
        buf[row * columns + x] = Math.round(hu);
      }
    }
    corBufs.push(buf.buffer);
  }

  // SAGITTAL: for each X col → image is rows wide x depth tall (Z flipped)
  const sagBufs = [];
  for (let x = 0; x < columns; x++) {
    const buf = new Int16Array(rows * depth);
    for (let z = 0; z < depth; z++) {
      const plane = planes[z];
      const row = (depth - 1 - z);
      for (let y = 0; y < rows; y++) {
        const hu = plane[y * columns + x] * slope + intercept;
        buf[row * rows + y] = Math.round(hu);
      }
    }
    sagBufs.push(buf.buffer);
  }

  self.postMessage({ corBufs, sagBufs }, [...corBufs, ...sagBufs]);
};
`;

  const blob = new Blob([workerCode], { type: 'application/javascript' });
  const url = URL.createObjectURL(blob);
  const worker = new Worker(url);
  worker.postMessage(
    { planes: pxArrays, rows, columns, depth, slope, intercept, corH, sagH },
    pxArrays.map(p => p.buffer)
  );

  // Save raw planes so WL presets can rebuild COR/SAG with new windowing
  if (!window.__mprRawPlanes) window.__mprRawPlanes = {};
  window.__mprRawPlanes[uid] = pxArrays;
  // Also save slope/intercept in meta for rebuild
  const _slope = slope; const _intercept = intercept;

  return new Promise(resolve => {
    worker.onmessage = e => {
      worker.terminate(); URL.revokeObjectURL(url);
      const { corBufs, sagBufs } = e.data;
      // COR slices — each is columns x depth (scaled to corH)
      window.__mprSliceCache[uid + '_cor'] = corBufs.map(buf => ({
        hu: new Int16Array(buf), w: columns, h: depth, scaledH: corH
      }));
      // SAG slices — each is rows x depth (scaled to sagH)
      window.__mprSliceCache[uid + '_sag'] = sagBufs.map(buf => ({
        hu: new Int16Array(buf), w: rows, h: depth, scaledH: sagH
      }));
      window.__mprSliceCache[uid + '_meta'] = {
        rows, columns, depth, ww, wc,
        dicomWW: dicomWW, dicomWC: dicomWC,
        rowSp, colSp, sliceSp, slope: _slope, intercept: _intercept,
        axialMid: Math.floor(depth   / 2),
        corMid:   Math.floor(rows    / 2),
        sagMid:   Math.floor(columns / 2),
      };
      resolve(true);
    };
    worker.onerror = err => {
      worker.terminate(); URL.revokeObjectURL(url);
      console.error('MPR worker:', err); resolve(false);
    };
  });
}
